import { Button, Group, Select, Stack, TextInput } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { i18nKeys, useTranslation } from 'locales';
import { useSelector } from 'react-redux';
import type { StoreState } from 'types/storeTypes';
import { z } from 'zod/v4';
import type { Reason } from '~/modules/Settings/Variables/Reasons/api';

export type ReasonFormValues = {
  name: string;
  reason_type: string;
};

type ReasonFormProps = {
  reason?: Reason;
  selectedType?: string;
  onSubmit: (values: ReasonFormValues) => void;
  onCancel?: () => void;
};

export const ReasonForm = ({ reason, onSubmit, onCancel, selectedType }: ReasonFormProps) => {
  const { t } = useTranslation();

  const { reason_types } = useSelector((state: StoreState) => state.app.constants);

  const requiredMessage = t(i18nKeys.ERROR.FIELD_REQUIRED);
  const validationSchema = z.object({
    name: z.string().min(1, { message: requiredMessage }),
    reason_type: z.string().min(1, { message: requiredMessage }),
  });

  const form = useForm<ReasonFormValues>({
    mode: 'controlled',
    initialValues: {
      name: reason?.name ?? '',
      reason_type: selectedType ?? reason?.reasonType ?? '',
    },
    validate: schemaResolver(validationSchema, { sync: true }),
  });

  return (
    <form onSubmit={form.onSubmit(values => onSubmit(values))}>
      <Stack gap="md">
        <TextInput
          label={t(i18nKeys.NAME)}
          placeholder={t(i18nKeys.FORM.ENTER_NAME)}
          {...form.getInputProps('name')}
        />
        <Select
          label={t(i18nKeys.FORM.TYPE)}
          placeholder={t(i18nKeys.FORM.SELECT_TYPE)}
          disabled={!!selectedType}
          data={reason_types.map(({ description, value }) => ({
            label: description,
            value,
          }))}
          {...form.getInputProps('reason_type')}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onCancel}>
            {t(i18nKeys.CANCEL)}
          </Button>
          <Button type="submit">{t(i18nKeys.SAVE)}</Button>
        </Group>
      </Stack>
    </form>
  );
};
